import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  getFleetSignalEndpoint,
  getTenantSignalEndpoint,
  listFleetSignalsEndpoint,
  type FleetSignalDetail,
  type FleetSignalSummary,
  type SignalDetail,
} from "../../client-messages/signals";
import { activationFiltersKey, type FleetActivationFilters } from "./activation-filters";
import { useCursorPager, useSkipEmptyPages } from "./cursor-pager";
import { SIGNALS_PAGE_SIZE, type UseFleetListResult } from "./hooks";

const listFleetSignalsQuery = queryFor(listFleetSignalsEndpoint);
const getTenantSignalQuery = queryFor(getTenantSignalEndpoint);
const getFleetSignalQuery = queryFor(getFleetSignalEndpoint);

/** Filtered exactly as the fleet activations are: a time window, and the start of a client number. */
export type FleetSignalFilters = FleetActivationFilters;

export type UseFleetSignalsResult = UseFleetListResult<FleetSignalSummary>;

export interface UseFleetSignalsOptions {
  readonly filters: FleetSignalFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

/**
 * One page of the fleet's Patriot signals, newest first, unmarked ones
 * included and log-ons left out unless a client is asked for.
 *
 * Paged like `useFleetActivations`, skipping empty pages the same way.
 * Changing any filter returns to page one.
 */
export function useFleetSignals({ filters, enabled = true }: UseFleetSignalsOptions): UseFleetSignalsResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(activationFiltersKey(filters));

  const query = useQuery({
    ...listFleetSignalsQuery(
      apiConfig,
      {},
      {
        limit: SIGNALS_PAGE_SIZE,
        ...filters,
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });
  const skipping = useSkipEmptyPages(pager, query.data, query.isPlaceholderData);

  return {
    items: skipping ? undefined : query.data?.items,
    clientNos: query.isPlaceholderData ? undefined : query.data?.clientNos,
    isLoading: query.isLoading || skipping,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    // As in `useTenantActivations`: a placeholder's cursor belongs to the page being left.
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}

export interface UseSignalResult<T = SignalDetail> {
  readonly signal: T | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/** One signal by Patriot's id from the tenant route, which serves it only to the tenant its marker names. */
export function useTenantSignal(signalId: string): UseSignalResult {
  const apiConfig = useApiConfig();
  const query = useQuery({ ...getTenantSignalQuery(apiConfig, { signalId }), enabled: signalId !== "" });
  return { signal: query.data, isLoading: query.isLoading, error: query.error, refetch: () => void query.refetch() };
}

/** One signal by Patriot's id from the fleet route, whichever tenant it belongs to — or none. */
export function useFleetSignal(signalId: string): UseSignalResult<FleetSignalDetail> {
  const apiConfig = useApiConfig();
  const query = useQuery({ ...getFleetSignalQuery(apiConfig, { signalId }), enabled: signalId !== "" });
  return { signal: query.data, isLoading: query.isLoading, error: query.error, refetch: () => void query.refetch() };
}
